import { SyntheticEvent, useEffect, useState } from "react"
import create from "zustand"

import { SnackBar } from "components/SnackBar"

type Severity = "success" | "info" | "warning" | "error"

interface Message {
  message: string
  severity: Severity | undefined
  key: number
}

type FeedbackState = {
  pack: Message[]
  //
  send: (message: string, severity?: Severity) => void
  clear: () => void
  pop: () => Message | undefined
}

export const useFeedback = create<FeedbackState>((set, get) => ({
  pack: [],
  //
  send: (message: string, severity?: Severity) =>
    set((s) => ({
      pack: [
        ...s.pack,
        {
          message,
          severity: severity,
          key: Date.now(),
        },
      ],
    })),
  clear: () => set((_) => ({ pack: [] })),
  pop: () => {
    const m = get().pack[0]
    set((s) => ({ pack: s.pack.slice(1) }))
    return m
  },
}))

export function useSendFeedBack() {
  return useFeedback((s) => s.send)
}

export default function FeedbackSnackbar() {
  const { pack, pop, clear } = useFeedback()
  const [open, setOpen] = useState<boolean>(() => false)
  const [active, setActive] = useState<Message | null>(() => null)
  const ln = pack.length
  const hasActive = !!active

  useEffect(() => {
    if (!ln) return
    if (hasActive && open) {
      setOpen(false)
    } else if (!hasActive) {
      const next = pop()
      setActive((_) => next || null)
      setOpen(true)
    }
  }, [ln, pop, hasActive, open])

  // clear everything on unmount
  useEffect(() => {
    return () => {
      clear()
      setOpen(false)
      setActive(null)
    }
  }, [clear])

  const onClose = (_?: SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") return
    setOpen(false)
  }

  const onExited = () => {
    setActive(null)
  }

  return (
    <SnackBar
      open={open}
      key={active?.key}
      message={active?.message || ""}
      severity={active?.severity}
      onClose={onClose}
      onExited={onExited}
    />
  )
}
